import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { MainActions } from '../../actions/MainActions';
import { SessionHelper } from '../../helpers/SessionHelper';
import { OrderType } from '../../enums/OrderType';
import { CustomerGeneralOption } from '../../enums/CustomerGeneralOption';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { FilterCustomerInput } from '../../services/Customers/FilterCustomer/FilterCustomerInput';
import { Customer } from '../../models/Customer';
import { Body } from '../../common-components/Layout/Body/Body';
import { Header } from '../../common-components/Layout/Header/Header';
import { Page } from '../../common-components/Layout/Page/Page';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { Pagination } from '../../common-components/Pagination/Pagination';
import { If } from '../../common-components/If/If';
import { ActionsPanel } from '../../components/ActionsPanel/ActionsPanel';
import { ModalCustomerRegistration } from '../../components/ModalCustomerRegistration/ModalCustomerRegistration';
import { ModalCustomerNotes } from '../../components/ModalCustomerNotes/ModalCustomerNotes';
import { AppliedFiltersToListCustomers } from './AppliedFiltersToListCustomers/AppliedFiltersToListCustomers';
import { ActionsForManageCustomers } from './ActionsForManageCustomers/ActionsForManageCustomers';
import { CustomerListItem } from './CustomerListItem/CustomerListItem';
import { IFilterToListCustomersOutput, ModalFiltersToListCustomers } from './ModalFiltersToListCustomers/ModalFiltersToListCustomers';

const ManageCustomers: React.FC = () => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [version, setVersion] = React.useState<number>(1);
  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(0);
  const [amountOfPages, setAmountOfPages] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [filtering, setFiltering] = React.useState<boolean>(true);
  const [lastFilter, setLastFilter] = React.useState<FilterCustomerInput | null>(null);
  const [lastFilterValues, setLastFilterValues] = React.useState<IFilterToListCustomersOutput | undefined>(undefined);
  const [modalFilterVisible, setModalFilterVisible] = React.useState<boolean>(false);
  const [modalCustomerId, setModalCustomerId] = React.useState<number | undefined>(undefined);
  const [modalRegistrationVisible, setModalRegistrationVisible] = React.useState<boolean>(false);
  const [modalCustomerNotesVisible, setModalCustomerNotesVisible] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const {
    filterCustomerService,
    toastService,
    getCustomerByIdService
  } = React.useContext(ServicesContext);

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => {
    const memo: IBreadcrumbItem[] = [
      {
        text: strings.Home,
        route: routes.Home
      }
    ];
    return memo;
  }, []);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    clearFilter(pageSize, currentPage);
  }, [session, navigate]);

  const headerRender = React.useMemo(() =>
    <Header
      title={strings.Customers}
      breadcrumbItems={breadcrumbItems}
      version={version}
      alertSummary={false}
    />
    , [version]);

  const contentHeader = React.useMemo(() => {
    const numberAppliedFilter =
      (lastFilterValues?.document ? 1 : 0) +
      (lastFilterValues?.name ? 1 : 0) +
      (lastFilterValues?.email ? 1 : 0) +
      (lastFilterValues?.phone ? 1 : 0) +
      (lastFilterValues?.creationStartDate ? 1 : 0) +
      (lastFilterValues?.creationEndDate ? 1 : 0) +
      (lastFilterValues?.lastModificationStartDate ? 1 : 0) +
      (lastFilterValues?.lastModificationEndDate ? 1 : 0);

    return (
      <>
        <AppliedFiltersToListCustomers appliedFilters={lastFilterValues} />
        <ActionsPanel>
          <ActionsForManageCustomers
            filtering={filtering}
            appliedFilters={numberAppliedFilter}
            updateList={() => updateList(pageSize, currentPage)}
            filterList={() => setModalFilterVisible(true)}
            clearFilter={() => clearFilter(pageSize, currentPage)}
            newRegister={() => setModalRegistrationVisible(true)}
          />
        </ActionsPanel>
      </>
    );
  }, [currentPage, currentPageSize, amountOfPages, totalAmount, pageSize, filtering, lastFilterValues]);

  const contentBody = React.useMemo(() => {
    if (!customers || customers.length === 0) return null;

    return customers.map(customer => <CustomerListItem key={`customer-${customer.id}`} customer={customer} handleGeneralOption={handleGeneralOption} />);
  }, [customers, filtering]);

  const contentFooter = React.useMemo(() =>
    <Pagination
      currentPage={currentPage}
      currentPageSize={currentPageSize}
      amountOfPages={amountOfPages}
      totalAmount={totalAmount}
      pageSize={pageSize}
      changePage={changePage}
      changePageSize={changePageSize}
      filtering={filtering}
    />
    , [currentPage, currentPageSize, amountOfPages, totalAmount, pageSize, filtering]);

  return (
    <>
      <Page header={headerRender} contentHeader={contentHeader} contentFooter={contentFooter}>
        <Body>
          {contentBody}
        </Body>
      </Page>
      <If condition={modalFilterVisible}>
        <ModalFiltersToListCustomers
          appliedFilters={lastFilterValues}
          close={() => setModalFilterVisible(false)}
          finish={(values) => changeFilter(values)}
        />
      </If>
      <If condition={modalRegistrationVisible}>
        <ModalCustomerRegistration
          editCustomerId={modalCustomerId}
          close={(changed, customerId) => handleCustomerRegistration(changed, customerId)}
        />
      </If>
      <If condition={modalCustomerNotesVisible}>
        <ModalCustomerNotes
          visible={true}
          close={() => closeModalCustomerNotes()}
          title={`${strings.Notes}`}
          customerId={modalCustomerId}
        />
      </If>
    </>
  );

  function changePage(page: number) {
    setCurrentPage(page);
    updateList(pageSize, page);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    setCurrentPage(1);
    updateList(size, 1);
  }

  function clearFilter(size: number, page: number) {
    setCurrentPage(page);
    const inititalFilter = {};
    setLastFilterValues(inititalFilter);
    filter(inititalFilter, size, page);
  }

  function updateList(size: number, page: number) {
    if (SessionHelper.isExpired(session)) {
      navigate(routes.Login);
      return;
    }
    setCurrentPage(page);
    filter(lastFilter || {}, size, page);
  }

  function changeFilter(values: IFilterToListCustomersOutput) {
    setModalFilterVisible(false);
    setCurrentPage(1);
    setLastFilterValues(values);
    let pageSizeValue = pageSize;
    filter(values, pageSizeValue, 1);
  }

  function filter(values: IFilterToListCustomersOutput, size: number, page: number) {
    mainActions.setNotWait(true);
    const filterInput = new FilterCustomerInput();
    filterInput.pageSize = size > 0 ? size : 1;
    filterInput.currentPage = page;
    filterInput.shouldPaginate = size > 0;
    filterInput.orderBy = 'CreatedAt';
    filterInput.orderType = OrderType.Descending;
    if (values.name) {
      filterInput.orderBy = 'Name';
      filterInput.orderType = OrderType.Ascending;
    } else if (values.lastModificationEndDate) {
      filterInput.orderBy = 'UpdatedAt';
    }
    filterInput.document = values.document;
    filterInput.name = values.name;
    filterInput.email = values.email;
    filterInput.phone = values.phone;
    filterInput.creationStartDate = values.creationStartDate ? moment(values.creationStartDate).format('yyyy-MM-DD') : undefined;
    filterInput.creationEndDate = values.creationEndDate ? moment(values.creationEndDate).format('yyyy-MM-DD') : undefined;
    filterInput.lastModificationStartDate = values.lastModificationStartDate ? moment(values.lastModificationStartDate).format('yyyy-MM-DD') : undefined;
    filterInput.lastModificationEndDate = values.lastModificationEndDate ? moment(values.lastModificationEndDate).format('yyyy-MM-DD') : undefined;
    setLastFilter(filterInput);
    handleFilterOrder(filterInput);
  }

  function handleFilterOrder(filterInput: FilterCustomerInput) {
    mainActions.setNotWait(true);
    setFiltering(true);
    filterCustomerService.handle(filterInput)
      .then(result => {
        setCustomers(result?.value?.list || []);
        setCurrentPage(result?.value?.currentPage || 0);
        setCurrentPageSize(result?.value?.currentPageSize || 0);
        setAmountOfPages(result?.value?.amountOfPages || 0);
        setTotalAmount(result?.value?.totalAmount || 0);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setFiltering(false);
      });
  }

  async function handleCustomerRegistration(changed?: boolean, customerId?: number) {
    closeModal();
    setModalRegistrationVisible(false);
    if (!changed) return;
    if (customerId && customerId > 0) {
      const result = await getCustomerByIdService.handle({ id: customerId });
      const customer = result?.value?.customer;
      if (customer) {
        setCustomers(customers.map(c => c.id === customer.id ? customer : c));
        return;
      }
    }
    updateList(pageSize, currentPage);
  }

  function handleGeneralOption(customer: Customer, key: any) {
    if (key === CustomerGeneralOption.CustomerNotes) {
      openModal(customer.id);
      setModalCustomerNotesVisible(true);
      return;
    }
    if (key === CustomerGeneralOption.Edit) {
      openModal(customer.id);
      setModalRegistrationVisible(true);
      return;
    }
  }

  function openModal(customerId?: number) {
    setModalCustomerId(customerId);
  }

  function closeModal() {
    setModalCustomerId(undefined);
  }

  function closeModalCustomerNotes() {
    closeModal();
    setModalCustomerNotesVisible(false);
  }
}

export default ManageCustomers;